import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["variant", "appearance", "size", "loading", "loadingAriaLabel", "disabled", "iconLeading", "iconTrailing", "iconSize", "children"];
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Spinner } from "../Spinner/index.js";
var StyledSpinner = /*#__PURE__*/styled(Spinner).withConfig({
  displayName: "Button__StyledSpinner",
  componentId: "sc-1bbve8d-0"
})(["position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);margin-left:-8px;margin-top:-8px;transition:all 0.1s ease-in-out;"]);
// Primary filled styles
var getPrimaryFilledStyles = function getPrimaryFilledStyles(theme) {
  return {
    default: {
      color: theme.kallisto.color.text.static.textBrandContrastPrimary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundPrimaryDefault
    },
    hover: {
      color: theme.kallisto.color.text.static.textBrandContrastPrimary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundPrimaryHover
    },
    pressed: {
      color: theme.kallisto.color.text.static.textBrandContrastPrimary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundPrimaryPressed
    },
    disabled: {
      color: theme.kallisto.color.text.static.textDisabled,
      backgroundColor: theme.kallisto.color.background.static.backgroundDisabled
    },
    focus: {
      color: theme.kallisto.color.text.static.textBrandContrastPrimary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundPrimaryHover
    }
  };
};

// Secondary filled styles
var getSecondaryFilledStyles = function getSecondaryFilledStyles(theme) {
  return {
    default: {
      color: theme.kallisto.color.text.static.textBrandContrastSecondary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundSecondaryDefault
    },
    hover: {
      color: theme.kallisto.color.text.static.textBrandContrastSecondary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundSecondaryHover
    },
    pressed: {
      color: theme.kallisto.color.text.static.textBrandContrastSecondary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundSecondaryPressed
    },
    disabled: {
      color: theme.kallisto.color.text.static.textDisabled,
      backgroundColor: theme.kallisto.color.background.static.backgroundDisabled
    },
    focus: {
      color: theme.kallisto.color.text.static.textBrandContrastSecondary,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundSecondaryHover
    }
  };
};

// Primary outlined styles
var getPrimaryOutlinedStyles = function getPrimaryOutlinedStyles(theme) {
  return {
    default: {
      color: theme.kallisto.color.text.interactive.textPrimaryDefault,
      borderColor: theme.kallisto.color.border.interactive.borderPrimaryDefault,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    hover: {
      color: theme.kallisto.color.text.interactive.textPrimaryHover,
      borderColor: theme.kallisto.color.border.interactive.borderPrimaryHover,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    pressed: {
      color: theme.kallisto.color.text.interactive.textPrimaryPressed,
      borderColor: theme.kallisto.color.border.interactive.borderPrimaryPressed,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    disabled: {
      color: theme.kallisto.color.text.static.textDisabled,
      borderColor: theme.kallisto.color.border.static.borderDisabled,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    focus: {
      color: theme.kallisto.color.text.interactive.textPrimaryHover,
      borderColor: theme.kallisto.color.border.interactive.borderPrimaryHover,
      backgroundColor: theme.kallisto.color.common.white[1000]
    }
  };
};

// Secondary outlined styles
var getSecondaryOutlinedStyles = function getSecondaryOutlinedStyles(theme) {
  return {
    default: {
      color: theme.kallisto.color.text.interactive.textSecondaryDefault,
      borderColor: theme.kallisto.color.border.interactive.borderSecondaryDefault,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    hover: {
      color: theme.kallisto.color.text.interactive.textSecondaryHover,
      borderColor: theme.kallisto.color.border.interactive.borderSecondaryHover,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    pressed: {
      color: theme.kallisto.color.text.interactive.textSecondaryPressed,
      borderColor: theme.kallisto.color.border.interactive.borderSecondaryPressed,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    disabled: {
      color: theme.kallisto.color.text.static.textDisabled,
      borderColor: theme.kallisto.color.border.static.borderDisabled,
      backgroundColor: theme.kallisto.color.common.white[1000]
    },
    focus: {
      color: theme.kallisto.color.text.interactive.textSecondaryHover,
      borderColor: theme.kallisto.color.border.interactive.borderSecondaryHover,
      backgroundColor: theme.kallisto.color.common.white[1000]
    }
  };
};

// Primary ghost styles
var getPrimaryGhostStyles = function getPrimaryGhostStyles(theme) {
  return {
    default: {
      color: theme.kallisto.color.text.interactive.textPrimaryDefault,
      backgroundColor: 'transparent'
    },
    hover: {
      color: theme.kallisto.color.text.interactive.textPrimaryHover,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundNeutralHover
    },
    pressed: {
      color: theme.kallisto.color.text.interactive.textPrimaryPressed,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundNeutralPressed
    },
    disabled: {
      color: theme.kallisto.color.text.static.textDisabled,
      backgroundColor: 'transparent'
    },
    focus: {
      color: theme.kallisto.color.text.interactive.textPrimaryHover,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundNeutralHover
    }
  };
};

// Secondary ghost styles
var getSecondaryGhostStyles = function getSecondaryGhostStyles(theme) {
  return {
    default: {
      color: theme.kallisto.color.text.interactive.textSecondaryDefault,
      backgroundColor: 'transparent'
    },
    hover: {
      color: theme.kallisto.color.text.interactive.textSecondaryHover,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundNeutralHover
    },
    pressed: {
      color: theme.kallisto.color.text.interactive.textSecondaryPressed,
      backgroundColor: theme.kallisto.color.background.interactive.backgroundNeutralPressed
    },
    disabled: {
      color: theme.kallisto.color.text.static.textDisabled,
      backgroundColor: 'transparent'
    },
    focus: {
      color: theme.kallisto.color.text.interactive.textSecondaryHover,
      backgroundColor: 'transparent'
    }
  };
};
var getButtonStyles = function getButtonStyles(theme, variant, appearance) {
  if (variant === 'primary') {
    if (appearance === 'filled') return getPrimaryFilledStyles(theme);
    if (appearance === 'outlined') return getPrimaryOutlinedStyles(theme);
    return getPrimaryGhostStyles(theme);
  }

  // secondary variant
  if (appearance === 'filled') return getSecondaryFilledStyles(theme);
  if (appearance === 'outlined') return getSecondaryOutlinedStyles(theme);
  return getSecondaryGhostStyles(theme);
};
export var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'primary' : _ref$variant,
    _ref$appearance = _ref.appearance,
    appearance = _ref$appearance === void 0 ? 'filled' : _ref$appearance,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'regular' : _ref$size,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    _ref$loadingAriaLabel = _ref.loadingAriaLabel,
    loadingAriaLabel = _ref$loadingAriaLabel === void 0 ? 'Loading' : _ref$loadingAriaLabel,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    IconLeading = _ref.iconLeading,
    IconTrailing = _ref.iconTrailing,
    _ref$iconSize = _ref.iconSize,
    iconSize = _ref$iconSize === void 0 ? 20 : _ref$iconSize,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(StyledButton, _extends({
    "data-testid": "lmn-ds-btn",
    ref: ref,
    variant: variant,
    appearance: appearance,
    size: size,
    disabled: disabled,
    $loading: loading
  }, loading && {
    'aria-label': loadingAriaLabel
  }, props), loading && /*#__PURE__*/React.createElement(StyledSpinner, {
    variant: "accent",
    size: 16
  }), /*#__PURE__*/React.createElement(ButtonContent, {
    $loading: loading
  }, IconLeading && /*#__PURE__*/React.createElement(IconLeading, {
    size: iconSize
  }), children, IconTrailing && /*#__PURE__*/React.createElement(IconTrailing, {
    size: iconSize
  })));
});
Button.displayName = 'Button';
var ButtonContent = /*#__PURE__*/styled.span.withConfig({
  displayName: "Button__ButtonContent",
  componentId: "sc-1bbve8d-1"
})(["display:inline-flex;align-items:center;justify-content:center;gap:", ";visibility:", ";"], function (_ref2) {
  var theme = _ref2.theme;
  return theme.kallisto.space.space100;
}, function (props) {
  return props.$loading ? 'hidden' : 'visible';
});
var StyledButton = /*#__PURE__*/styled.button.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['variant', 'appearance', 'size', '$loading'].includes(prop);
  }
}).withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-1bbve8d-2"
})(["position:relative;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-family:", ";font-weight:", ";font-size:", ";line-height:", ";border-radius:", ";border:none;outline:none;box-sizing:border-box;text-align:center;text-decoration:none;padding:", " ", ";transition:color 0.1s ease-in-out,background-color 0.1s ease-in-out,border-color 0.1s ease-in-out,box-shadow 0.1s ease-in-out;", " ", " ", ""], function (_ref3) {
  var theme = _ref3.theme;
  return theme.kallisto.font.family.sans;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.kallisto.font.weight.medium;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.kallisto.font.size[200];
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.kallisto.font.lineHeight[300];
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.kallisto.border.radius.radius100;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.kallisto.space.space100;
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.kallisto.space.space200;
}, function (_ref0) {
  var theme = _ref0.theme,
    _ref0$appearance = _ref0.appearance,
    appearance = _ref0$appearance === void 0 ? 'filled' : _ref0$appearance;
  return appearance === 'outlined' && css(["border-style:solid;border-width:", ";padding:calc(", " - ", ") calc(", " - ", ");"], theme.kallisto.border.width.width12, theme.kallisto.space.space100, theme.kallisto.border.width.width12, theme.kallisto.space.space200, theme.kallisto.border.width.width12);
}, function (_ref1) {
  var theme = _ref1.theme,
    _ref1$variant = _ref1.variant,
    variant = _ref1$variant === void 0 ? 'primary' : _ref1$variant,
    _ref1$appearance = _ref1.appearance,
    appearance = _ref1$appearance === void 0 ? 'filled' : _ref1$appearance,
    $loading = _ref1.$loading;
  var styles = getButtonStyles(theme, variant, appearance);
  return css(["color:", ";background-color:", ";", " &:hover:not(:disabled){color:", ";background-color:", ";", "}&:active:not(:disabled){color:", ";background-color:", ";", "}&:focus-visible{outline:2px ", " solid;outline-offset:0;box-shadow:0 0 0 4px ", ";}", " &:disabled{color:", ";background-color:", ";", " cursor:not-allowed;user-select:none;}"], styles.default.color, styles.default.backgroundColor, styles.default.borderColor && "border-color: ".concat(styles.default.borderColor, ";"), styles.hover.color, styles.hover.backgroundColor, styles.default.borderColor && "border-color: ".concat(styles.hover.borderColor, ";"), styles.pressed.color, styles.pressed.backgroundColor, styles.default.borderColor && "border-color: ".concat(styles.pressed.borderColor, ";"), theme.kallisto.color.common.white[1000], theme.kallisto.color.common.black[1000], $loading && css(["color:", ";background-color:", ";", " pointer-events:none;"], styles.disabled.color, styles.disabled.backgroundColor, styles.default.borderColor && "border-color: ".concat(styles.disabled.borderColor, ";")), styles.disabled.color, styles.disabled.backgroundColor, styles.default.borderColor && "border-color: ".concat(styles.disabled.borderColor, ";"));
}, function (_ref10) {
  var theme = _ref10.theme,
    _ref10$size = _ref10.size,
    size = _ref10$size === void 0 ? 'regular' : _ref10$size,
    _ref10$appearance = _ref10.appearance,
    appearance = _ref10$appearance === void 0 ? 'filled' : _ref10$appearance;
  return size === 'large' && css(["padding:", ";"], appearance === 'outlined' ? "calc(".concat(theme.kallisto.space.space150, " - ").concat(theme.kallisto.border.width.width12, ") calc(").concat(theme.kallisto.space.space200, " - ").concat(theme.kallisto.border.width.width12, ")") : "".concat(theme.kallisto.space.space150, " ").concat(theme.kallisto.space.space200));
});